export const name = "EUCLIDEAN_DISTANCE";
export const summary = `Computes the Euclidean distance between two vectors.`;
export const reference = `
## EUCLIDEAN_DISTANCE

Computes the Euclidean distance between two vectors.

\`\`\`
EUCLIDEAN_DISTANCE(vector1, vector2)
\`\`\`

### Description

Computes the Euclidean distance between two vectors.


### Definitions

- \`vector1\`: A vector that's represented by an\`ARRAY<T>\` value or a sparse vector that is represented by an \`ARRAY<STRUCT<dimension,magnitude>>\` value.
- \`vector2\`: A vector that's represented by an\`ARRAY<T>\` value or a sparse vector that is represented by an \`ARRAY<STRUCT<dimension,magnitude>>\` value.


### Details

- \`ARRAY<T>\` can be used to represent a vector. Each zero-based index in this array represents a dimension. The value for each element in this array represents a magnitude.

- \`ARRAY<STRUCT<dimension,magnitude>>\` can be used to represent a sparse vector. With a sparse vector, you only need to include dimension-magnitude pairs for non-zero magnitudes. If a magnitude isn't present in the sparse vector, the magnitude is implicitly understood to be zero.

- Both non-sparse vectors in this function must share the same dimensions, and if they don't, an error is produced.

- A vector can be a zero vector. A vector is a zero vector if it has no dimensions or all dimensions have a magnitude of \`0\`, such as \`[]\` or\`[0.0, 0.0]\`.

- An error is produced if a magnitude in a vector is \`NULL\`.

- If a vector is \`NULL\`, \`NULL\` is returned.


### Return type

\`FLOAT64\`


### Examples

In the following example, non-sparse vectors are used to compute the Euclidean distance:



\`\`\`
SELECT EUCLIDEAN_DISTANCE([1.0, 2.0], [3.0, 4.0]) AS results;

/*----------*
 | results |
 +----------+
 | 2.828 |
 *----------*/
\`\`\`

In the following example, sparse vectors are used to compute the Euclidean distance:



\`\`\`
SELECT EUCLIDEAN_DISTANCE(
 [(1, 1.0), (2, 2.0)],
 [(2, 4.0), (1, 3.0)]) AS results;

 /*----------*
  | results |
  +----------+
  | 2.828 |
  *----------*/
\`\`\`

The ordering of magnitudes in a vector doesn't impact the results produced by this function. For example these queries produce the same results even though the magnitudes in each vector is in a different order:



\`\`\`
SELECT EUCLIDEAN_DISTANCE([1.0, 2.0], [3.0, 4.0]);
\`\`\`



\`\`\`
SELECT EUCLIDEAN_DISTANCE([2.0, 1.0], [4.0, 3.0]);
\`\`\`



\`\`\`
SELECT EUCLIDEAN_DISTANCE([(1, 1.0), (2, 2.0)], [(1, 3.0), (2, 4.0)]) AS results;
\`\`\`



\`\`\`
/*----------*
  | results |
  +----------+
  | 2.828 |
  *----------*/
\`\`\`

Both non-sparse vectors must have the same dimensions. If not, an error is produced. In the following example, the first vector has two dimensions and the second vector has three:



\`\`\`
-- ERROR
SELECT EUCLIDEAN_DISTANCE([9.0, 7.0], [8.0, 4.0, 5.0]) AS results;
\`\`\`

If you use sparse vectors and you repeat a dimension, an error is produced:



\`\`\`
-- ERROR
SELECT EUCLIDEAN_DISTANCE(
  [(1, 9.0), (2, 7.0), (2, 8.0)], [(1, 8.0), (2, 4.0), (3, 5.0)]) AS results;
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/mathematical_functions
`;
